import { Alpine } from '../../core/alpine-core.js'
import { apiFetch } from '../api.js'
import { fetchAdminGoogleMapsApiKey, loadGoogleMapsAdminSdk } from '../google-maps-sdk.js'

function fmtMapsUrl({ lat, lng, placeId }) {
    return placeId
        ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}&query_place_id=${encodeURIComponent(placeId)}`
        : `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
}
function fmtDirectionsUrl({ lat, lng, placeId }) {
    const base = `https://www.google.com/maps/dir/?api=1&destination=${lat}%2C${lng}`
    return placeId ? `${base}&destination_place_id=${encodeURIComponent(placeId)}` : base
}
function debounce(fn, ms = 400) {
    let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms) }
}

const DEFAULT_VENUE_LAT = 7.8804
const DEFAULT_VENUE_LNG = 98.3923
const DEFAULT_VENUE_MAP_ID = 'DEMO_MAP_ID'
const PLACE_SEARCH_INPUT_CLASS = 'w-full rounded px-3 py-2 border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 dark:placeholder:text-neutral-500'

function readLatLng(value) {
    if (!value) return null
    const lat = typeof value.lat === 'function' ? value.lat() : value.lat
    const lng = typeof value.lng === 'function' ? value.lng() : value.lng
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
    return { lat, lng }
}

function readPlaceDisplayName(place) {
    const name = place?.displayName
    if (typeof name === 'string') return name
    return String(name?.text || '').trim()
}

function coerceVenueCoord(value, fallback) {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
}

/** Module-level helper — safe for Alpine bindings on create/edit. */
function venueEditDisplayName(record) {
    if (record == null) return 'Unnamed venue'
    if (typeof record === 'string') return String(record).trim() || 'Unnamed venue'
    return String(record.name || '').trim() || 'Unnamed venue'
}

Alpine.data('venuesEdit', () => ({

    isEditing: false,
    saving: false,

    map: null,
    marker: null,
    geocoder: null,
    placeAutocomplete: null,
    /** Classic Marker when AdvancedMarkerElement / mapId unavailable. */
    usesLegacyMarker: false,
    /** Pin visible on map (hidden until click or place search on new venues). */
    markerPlaced: false,
    _mapClickListener: null,
    loading: true,
    mapsBlocked: false,
    /** no_key | invalid | load_failed */
    mapsBlockedReason: '',

    deleteOpen: false,
    deleteBusy: false,

    venueEditDisplayName,
    venueDisplayName: venueEditDisplayName,

    form: {
        _id: null,
        name: '',
        address: '',
        lat: 7.8804,          // Phuket default
        lng: 98.3923,
        mapsUrl: '',
        directionsUrl: '',
        imageUrl: '',
        active: true,
        placeId: '',
        streaming: {
            enabled: false,
            sourceType: 'rtsp',
            boardCamUrl: '',
            relayUrl: '',
            publicViewerUrl: '',
            label: '',
            notes: ''
        },
        phone: '',
        website: '',
        googleUrl: ''
    },

    readRouteId() {
        const url = new URL(location.href)
        const path = (url.hash && url.hash.startsWith('#/')) ? url.hash.slice(1) : url.pathname
        const parts = path.split('/').filter(Boolean)
        const idx = parts.indexOf('venues')
        if (idx >= 0 && parts[idx + 1]) {
            const seg = decodeURIComponent(parts[idx + 1])
            return seg === 'new' ? null : seg
        }
        return null
    },

    viewUrl() {
        if (!this.form._id) return '/venues'
        return `/admin/venues/${encodeURIComponent(this.form._id)}`
    },

    mapsBlockedTitle() {
        if (this.mapsBlockedReason === 'invalid') return 'Google Maps key rejected'
        if (this.mapsBlockedReason === 'load_failed') return 'Google Maps could not load'
        return 'Google Maps API key not configured'
    },

    mapsBlockedDetail() {
        if (this.mapsBlockedReason === 'invalid') {
            return 'Google refused this key (RefererNotAllowedMapError, billing, or API restrictions). In Google Cloud Console enable Maps JavaScript API and Places API, add HTTP referrers such as https://dartsos.com/* and http://localhost:*, then save the key again under Account settings.'
        }
        if (this.mapsBlockedReason === 'load_failed') {
            return 'The Maps script failed to load or timed out. Check network, billing, and API enablement, then retry.'
        }
        return 'Add a browser Maps JavaScript API key under Account settings → Subscription (Google Maps API section).'
    },

    setMarkerPosition(point) {
        if (!this.marker || !point) return
        if (this.usesLegacyMarker) {
            this.marker.setPosition(point)
            return
        }
        this.marker.position = point
    },

    shouldShowMarkerOnLoad() {
        if (!this.isEditing) return false
        const lat = Number(this.form.lat)
        const lng = Number(this.form.lng)
        return Number.isFinite(lat) && Number.isFinite(lng)
    },

    async ensureMarkerAt(point, { pan = true, zoom = 17 } = {}) {
        if (!this.map || !point) return

        if (!this.marker) {
            if (this.usesLegacyMarker) {
                this.marker = new google.maps.Marker({
                    map: this.map,
                    position: point,
                    draggable: true,
                })
            } else {
                const { AdvancedMarkerElement } = await google.maps.importLibrary('marker')
                this.marker = new AdvancedMarkerElement({
                    map: this.map,
                    position: point,
                    gmpDraggable: true,
                })
            }
            this.bindMarkerDragListener()
        } else {
            this.setMarkerPosition(point)
            if (this.usesLegacyMarker) {
                this.marker.setMap(this.map)
            } else {
                this.marker.map = this.map
            }
        }

        this.markerPlaced = true
        if (pan) {
            this.map.setCenter(point)
            const nextZoom = Math.max(Number(this.map.getZoom()) || 16, zoom)
            this.map.setZoom(nextZoom)
        }
    },

    removeMarker() {
        if (!this.marker) {
            this.markerPlaced = false
            return
        }
        if (this.usesLegacyMarker) {
            this.marker.setMap(null)
        } else {
            this.marker.map = null
        }
        this.marker = null
        this.markerPlaced = false
        this.$store.toastr.info('Pin removed', { duration: 1200 })
    },

    bindMarkerDragListener() {
        if (!this.marker) return
        this.marker.addListener('dragend', () => {
            const pos = this.usesLegacyMarker
                ? readLatLng(this.marker.getPosition())
                : readLatLng(this.marker.position)
            if (!pos) return
            this.form.lat = pos.lat
            this.form.lng = pos.lng
            this.reverseGeocode(pos.lat, pos.lng)
            this.updateLinks()
            this.$store.toastr.info('Pin moved', { duration: 1200 })
        })
    },

    bindMapClickListener() {
        if (!this.map || this._mapClickListener) return
        this._mapClickListener = this.map.addListener('click', (event) => {
            const pos = readLatLng(event.latLng)
            if (!pos) return
            this.placeMarkerAt(pos, { fromMapClick: true })
        })
    },

    async placeMarkerAt(point, { fromMapClick = false, pan = true, zoom = 17 } = {}) {
        await this.ensureMarkerAt(point, { pan, zoom })
        this.form.lat = point.lat
        this.form.lng = point.lng
        this.updateLinks()
        if (fromMapClick) {
            this.reverseGeocode(point.lat, point.lng)
            this.$store.toastr.info('Pin placed', { duration: 1200 })
        }
    },

    async applySelectedPlace({ name, address, lat, lng, placeId, website, googleUrl }) {
        if (name) this.form.name = name
        if (address) this.form.address = address
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            this.form.lat = lat
            this.form.lng = lng
        }
        if (placeId) this.form.placeId = placeId
        if (website) this.form.website = website
        if (googleUrl) this.form.googleUrl = googleUrl
        this.updateLinks()
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            if (!this.map) await this.ensureMap()
            await this.placeMarkerAt({ lat, lng }, { pan: true, zoom: 17 })
        }
        this.$store.toastr.success('Place selected')
    },

    async init(id = null) {
        this.mapsBlocked = false
        this.mapsBlockedReason = ''

        let apiKey = ''
        try {
            apiKey = await fetchAdminGoogleMapsApiKey()
        } catch {
            apiKey = ''
        }

        if (!apiKey) {
            this.mapsBlocked = true
            this.mapsBlockedReason = 'no_key'
        } else {
            try {
                await loadGoogleMapsAdminSdk(apiKey, { libraries: 'places' })
            } catch (e) {
                this.mapsBlocked = true
                const code = e?.code || (e?.message === 'auth_failed' ? 'AUTH_FAILED' : '')
                this.mapsBlockedReason = code === 'AUTH_FAILED' || e?.message === 'auth_failed' ? 'invalid' : 'load_failed'
            }
        }

        const routeId = id ?? this.readRouteId()
        this.isEditing = Boolean(routeId)

        if (this.isEditing) {
            await this.load(routeId)
        } else {
            this.updateLinks()
        }

        this.$watch('loading', (isLoading) => {
            if (this.mapsBlocked) return
            if (!isLoading && this.map) {
                this.$nextTick(() => {
                    google.maps.event.trigger(this.map, 'resize')
                    this.map.setCenter({ lat: Number(this.form.lat) || 7.8804, lng: Number(this.form.lng) || 98.3923 })
                })
            } else if (!isLoading) {
                this.ensureMap()
            }
        })

        if (!this.mapsBlocked) {
            await this.setupAutocomplete()
        }

        this.loading = false
    },

    /** wait until map div exists, has size, and google is ready */
    async ensureMap() {
        if (this.mapsBlocked) return
        if (this.map) return
        try {
            await this.waitFor(() => !!this.$refs.map, 50, 60)

            // If height is still 0, set a fallback height.
            if (this.$refs.map.offsetHeight === 0) {
                this.$refs.map.style.height = '420px'
            }
            await this.waitFor(() => this.$refs.map.offsetHeight > 0, 50, 60)

            await this.waitFor(() => !!(window.google && google.maps && google.maps.importLibrary), 100, 60)
            await this.initMap()

            // If initialized while hidden, force a resize & recenter soon after.
            this.$nextTick(() => {
                setTimeout(() => {
                    google.maps.event.trigger(this.map, 'resize')
                    this.map.setCenter({ lat: Number(this.form.lat) || 7.8804, lng: Number(this.form.lng) || 98.3923 })
                }, 50)
            })
        } catch {
            // swallow; later triggers (loading flips or UI changes) will retry
        }
    },

    /** generic retry helper */
    waitFor(testFn, tries = 50, delayMs = 50) {
        return new Promise((resolve, reject) => {
            const tick = () => {
                if (testFn()) return resolve()
                if (--tries <= 0) return reject(new Error('waitFor: timeout'))
                setTimeout(tick, delayMs)
            }
            tick()
        })
    },

    async initMap() {
        if (!this.$refs.map) return
        const center = { lat: Number(this.form.lat) || DEFAULT_VENUE_LAT, lng: Number(this.form.lng) || DEFAULT_VENUE_LNG }

        this.usesLegacyMarker = false
        this.marker = null
        this.markerPlaced = false

        try {
            const { Map } = await google.maps.importLibrary('maps')
            await google.maps.importLibrary('marker')

            this.map = new Map(this.$refs.map, {
                mapId: DEFAULT_VENUE_MAP_ID,
                center,
                zoom: 16,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: true,
            })
        } catch {
            this.usesLegacyMarker = true
            this.map = new google.maps.Map(this.$refs.map, {
                center,
                zoom: 16,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: true,
            })
        }

        this.geocoder = new google.maps.Geocoder()
        this.bindMapClickListener()

        if (this.shouldShowMarkerOnLoad()) {
            await this.ensureMarkerAt(center, { pan: false, zoom: 16 })
        }
    },

    async load(id) {
        try {
            const res = await apiFetch(`/venues/${id}`)
            const data = await res.json()
            if (!res.ok) throw new Error(data.message || data.error || 'Failed to load venue')

            const v = data.venue || data
            this.form = {
                _id: v._id || v.id || id,
                name: v.name || '',
                address: v.address || '',
                lat: coerceVenueCoord(v.lat, DEFAULT_VENUE_LAT),
                lng: coerceVenueCoord(v.lng, DEFAULT_VENUE_LNG),
                mapsUrl: v.mapsUrl || '',
                directionsUrl: v.directionsUrl || '',
                imageUrl: v.imageUrl || '',
                active: v.active ?? true,
                placeId: v.placeId || '',
                streaming: {
                    enabled: !!v.streaming?.enabled,
                    sourceType: v.streaming?.sourceType || 'rtsp',
                    boardCamUrl: v.streaming?.boardCamUrl || '',
                    relayUrl: v.streaming?.relayUrl || '',
                    publicViewerUrl: v.streaming?.publicViewerUrl || '',
                    label: v.streaming?.label || '',
                    notes: v.streaming?.notes || ''
                },
                phone: v.phone || '',
                website: v.website || '',
                googleUrl: v.googleUrl || ''
            }
        } catch (e) {
            this.$store.toastr.error(e.message || 'Failed to load venue')
        }
    },

    async setupLegacyAutocomplete(host) {
        host.replaceChildren()
        const input = document.createElement('input')
        input.id = 'place-autocomplete'
        input.type = 'text'
        input.autocomplete = 'off'
        input.placeholder = 'Type venue name or address…'
        input.className = PLACE_SEARCH_INPUT_CLASS
        host.appendChild(input)
        host.dataset.placesReady = '1'
        host.dataset.placesMode = 'legacy'

        await this.waitFor(() => !!(google.maps?.places?.Autocomplete), 100, 80)

        const ac = new google.maps.places.Autocomplete(input, {
            fields: ['place_id', 'name', 'formatted_address', 'geometry', 'url', 'website', 'formatted_phone_number'],
            types: ['establishment', 'geocode'],
        })
        this.placeAutocomplete = ac

        ac.addListener('place_changed', () => {
            const place = ac.getPlace()
            const loc = readLatLng(place?.geometry?.location)
            if (!loc) return
            this.applySelectedPlace({
                name: place.name || '',
                address: place.formatted_address || '',
                lat: loc.lat,
                lng: loc.lng,
                placeId: place.place_id || '',
                website: place.website || '',
                googleUrl: place.url || '',
            })
        })
    },

    async setupAutocomplete() {
        if (this.mapsBlocked) return
        const host = document.getElementById('place-autocomplete-host')
        if (!host || host.dataset.placesReady === '1') return

        await this.waitFor(() => !!(window.google?.maps?.importLibrary), 100, 80)

        try {
            const { PlaceAutocompleteElement } = await google.maps.importLibrary('places')
            host.replaceChildren()

            const el = new PlaceAutocompleteElement({})
            el.style.width = '100%'
            let fellBack = false
            const fallback = () => {
                if (fellBack) return
                fellBack = true
                host.dataset.placesReady = '0'
                this.placeAutocomplete = null
                this.setupLegacyAutocomplete(host)
            }

            el.addEventListener('gmp-error', fallback)
            el.addEventListener('gmp-placeselect', async ({ place }) => {
                try {
                    await place.fetchFields({
                        fields: ['id', 'displayName', 'formattedAddress', 'location', 'googleMapsURI', 'websiteURI', 'nationalPhoneNumber'],
                    })
                    const loc = readLatLng(place.location)
                    if (!loc) return
                    this.applySelectedPlace({
                        name: readPlaceDisplayName(place),
                        address: place.formattedAddress || '',
                        lat: loc.lat,
                        lng: loc.lng,
                        placeId: place.id || '',
                        website: place.websiteURI || '',
                        googleUrl: place.googleMapsURI || '',
                    })
                } catch {
                    fallback()
                }
            })

            host.appendChild(el)
            host.dataset.placesReady = '1'
            host.dataset.placesMode = 'new'
            this.placeAutocomplete = el
        } catch {
            await this.setupLegacyAutocomplete(host)
        }
    },

    reverseGeocode: debounce(function (lat, lng) {
        if (!this.geocoder) return
        this.geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results && results[0]) {
                this.form.address = results[0].formatted_address
                const placeId = results[0].place_id || ''
                if (placeId) this.form.placeId = placeId
                this.updateLinks()
            }
        })
    }, 500),

    onLatLngManual() {
        if (!this.map) return
        const p = { lat: Number(this.form.lat), lng: Number(this.form.lng) }
        if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return
        this.placeMarkerAt(p, { pan: true, zoom: 16 })
        this.reverseGeocode(p.lat, p.lng)
        this.$store.toastr.info('Coordinates updated', { duration: 1200 })
    },

    async centerOnForm() {
        if (this.mapsBlocked || !this.map) return
        const p = { lat: Number(this.form.lat), lng: Number(this.form.lng) }
        if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return
        this.map.setCenter(p)
        if (this.markerPlaced) {
            await this.ensureMarkerAt(p, { pan: false, zoom: Number(this.map.getZoom()) || 16 })
        }
    },

    updateLinks() {
        const { lat, lng, placeId } = this.form
        this.form.mapsUrl = fmtMapsUrl({ lat, lng, placeId })
        this.form.directionsUrl = fmtDirectionsUrl({ lat, lng, placeId })
    },

    async save() {
        this.saving = true
        try {
            const lat = coerceVenueCoord(this.form.lat, DEFAULT_VENUE_LAT)
            const lng = coerceVenueCoord(this.form.lng, DEFAULT_VENUE_LNG)
            const name = String(this.form.name || '').trim()
            const payload = {
                name,
                address: this.form.address?.trim(),
                lat,
                lng,
                mapsUrl: this.form.mapsUrl?.trim(),
                directionsUrl: this.form.directionsUrl?.trim(),
                imageUrl: this.form.imageUrl?.trim(),
                active: !!this.form.active,
                placeId: this.form.placeId || undefined,
                streaming: {
                    enabled: !!this.form.streaming?.enabled,
                    sourceType: this.form.streaming?.sourceType || 'rtsp',
                    boardCamUrl: this.form.streaming?.boardCamUrl?.trim() || undefined,
                    relayUrl: this.form.streaming?.relayUrl?.trim() || undefined,
                    publicViewerUrl: this.form.streaming?.publicViewerUrl?.trim() || undefined,
                    label: this.form.streaming?.label?.trim() || undefined,
                    notes: this.form.streaming?.notes?.trim() || undefined
                },
                phone: this.form.phone || undefined,
                website: this.form.website || undefined,
                googleUrl: this.form.googleUrl || undefined
            }

            if (!name) {
                throw new Error('Venue name is required.')
            }

            this.form.lat = lat
            this.form.lng = lng
            this.updateLinks()

            if (this.isEditing) {
                const r = await apiFetch(`/venues/${this.form._id}`, { method: 'PATCH', body: payload })
                const data = await r.json().catch(() => ({}))
                if (!r.ok) throw new Error(data.message || data.error || 'Update failed')
                this.$store.toastr.success('Venue updated')
            } else {
                const created = await apiFetch('/venues', { method: 'POST', body: payload })
                const data = await created.json().catch(() => ({}))
                if (!created.ok) throw new Error(data.message || data.error || 'Create failed')
                this.$store.toastr.success('Venue created')

                const router = globalThis.PineconeRouter
                if (router?.navigate) router.navigate('/admin/venues')
                else globalThis.location.href = '/admin/venues'
            }
        } catch (e) {
            this.$store.toastr.error(e.message || 'Failed to save venue')
        } finally {
            this.saving = false
        }
    },

    confirmDelete() { this.deleteOpen = true; },
    async doDelete() {
        if (!this.form._id || this.deleteBusy) return;
        this.deleteBusy = true;
        try {
            const res = await apiFetch(`/venues/${this.form._id}`, { method: 'DELETE' });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.message || 'Delete failed');

            this.$store.toastr.success(`Venue ${this.form.name} deleted`);
            this.deleteOpen = false;
            this.deleteBusy = false;
            location.href = '/admin/venues';

        } catch (e) {
            this.$store.toastr.error(e.message || 'Delete failed');
            this.deleteOpen = false;
        } finally {
            this.deleteBusy = false;
        }
    },

}))
